import React from 'react';

import { LayoutColors } from '@src/core/theme';
import styled, { css } from 'styled-components';
import { ScheduleTime } from '@src/core/buyerApiClient';
import { useTranslate, useWidgetsTheme as theme } from '@src/core/hooks';
import { BodyRegular, BodyRegularMedium, BodyRegularSemiBold, BodySmallMedium } from '@src/fonts/typography/Typography';

type MorningTimeType = {
  hour: ScheduleTime;
  error: boolean;
};

type NightTimesType = {
  hour: ScheduleTime;
  error: boolean;
};

type Props = {
  hidden?: boolean;
  selectedTime?: ScheduleTime;
  nightTimes: NightTimesType[];
  morningTimes: MorningTimeType[];
  areAllTimesUnavailable?: boolean;
  onSelectTime: (time: ScheduleTime, error: boolean) => void;
};

type TimeCardProps = {
  background: string;
  hasError: boolean;
  existselectedTime: boolean;
  selected?: boolean;
};

const TimeSelector = (props: Props) => {
  const {
    nightTimes,
    morningTimes,
    hidden = false,
    onSelectTime,
    selectedTime,
    areAllTimesUnavailable = false,
  } = props;

  const { i18n } = useTranslate();
  const successColor = theme('successColorDark');
  const hasOnlyOneShift = morningTimes.length === 0 || nightTimes.length === 0;

  return (
    <TimeSelectionArea hidden={hidden}>
      <TimeSelectionAreaLabel>{i18n('adder_hour')}</TimeSelectionAreaLabel>
      {areAllTimesUnavailable ? (
        <ProductAdderErrorLabel>{i18n('adder_hours_unavailable_error')}</ProductAdderErrorLabel>
      ) : (
        <>
          {[morningTimes, nightTimes].map((hours, index) => (
            <div key={index} hidden={!hours?.length}>
              <TimeOfTheDayLabel>
                {hasOnlyOneShift ? null : index === 0 ? i18n('adder_day') : i18n('adder_night')}
              </TimeOfTheDayLabel>
              <HoursArea>
                {hours?.map(({ hour, error }) => (
                  <TimeCard
                    key={hour.formattedTime}
                    background={successColor}
                    hasError={error}
                    existselectedTime={!!selectedTime}
                    selected={hour.formattedTime == selectedTime?.formattedTime}
                    onClick={() => onSelectTime(hour, error)}
                    data-cy={error ? 'unavailableTimeCard' : 'availableTimeCard'}
                  >
                    {hour.formattedTime}
                  </TimeCard>
                ))}
              </HoursArea>
            </div>
          ))}
        </>
      )}
    </TimeSelectionArea>
  );
};

const TimeSelectionArea = styled.div`
  background: #f6f6f6;
  padding: 16px;
  border-radius: 8px;
`;
const TimeSelectionAreaLabel = styled.p`
  ${BodyRegularSemiBold}
  color: ${LayoutColors.blackDark};
  text-transform: uppercase;
`;

const selectedTimeCard = css`
  border: 4px solid #384e45;
  opacity: 1;
`;

const TimeCard = styled.div<TimeCardProps>`
  ${BodyRegular}
  color: ${LayoutColors.whiteLight};
  background-color: ${(props) => (props.hasError ? LayoutColors.blackLight : LayoutColors.successMain)};
  opacity: ${(props) => (props.existselectedTime ? '0.5' : '1')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.hasError ? 'not-allowed' : 'pointer')};
  width: 60px;
  height: 40px;
  border-radius: 4px;
  box-sizing: border-box;
  ${(props) => (props.selected ? selectedTimeCard : null)};
`;
const HoursArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const TimeOfTheDayLabel = styled.p`
  ${BodyRegularMedium}
  margin-top: 16px;
  margin-bottom: 8px;
`;

const ProductAdderErrorLabel = styled.p`
  ${BodySmallMedium};
  padding: 8px;
  background-color: #ffd2cd;
  border-radius: 4px;
  margin-top: 8px;
  color: #c7372c;
`;

export default TimeSelector;
